import React, { useCallback, useState } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft, Compass, Plus, Shield, AlertTriangle, Heart,
  TrendingUp, Trash2, BookOpen, CheckCircle2,
} from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Skeleton } from '../../components/ui/Skeleton'
import { EmptyState } from '../../components/EmptyState'
import { RowActionsMenu } from '../../components/ui/RowActionsMenu'
import PullToRefresh from '../../components/PullToRefresh'
import { useAuth } from '../../contexts/AuthContext'
import { useConfirm } from '../../contexts/ConfirmDialogContext'
import { isOwner } from '../../lib/owner'
import { useUrges, useCheckins, useJourneyStats } from '../../hooks/useJourney'
import { performRefresh } from '../../lib/refresh'
import VoiceJournalCard from './components/VoiceJournalCard'
import { cn } from '../../lib/utils'

function num(n, decimals = 0) {
  if (n == null || isNaN(n)) return '—'
  return Number(n).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

// ----- Quick log card: log an urge in 3 taps --------------------------
function UrgeForm({ onSave, onCancel, submitting }) {
  const { t } = useTranslation()
  const [intensity, setIntensity] = useState(3)
  const [triggerText, setTriggerText] = useState('')
  const [response, setResponse] = useState('resisted')
  const [notes, setNotes] = useState('')

  const submit = (e) => {
    e.preventDefault()
    onSave({
      intensity,
      trigger_text: triggerText.trim() || null,
      response,
      notes: notes.trim() || null,
    })
  }

  return (
    <form onSubmit={submit} className="space-y-3 p-3 rounded-lg border border-input bg-muted/30">
      <div>
        <p className="text-xs text-muted-foreground mb-1" dir="auto">{t('journey.urge.intensity')}</p>
        <div className="grid grid-cols-5 gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setIntensity(n)}
              aria-pressed={intensity === n}
              className={cn(
                'py-2 rounded-md border text-sm transition-colors',
                intensity === n
                  ? 'border-app-primary-500 bg-app-primary-500/10 text-app-primary-500'
                  : 'border-input text-muted-foreground hover:bg-accent',
              )}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs text-muted-foreground mb-1" dir="auto">{t('journey.urge.response')}</p>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setResponse('resisted')}
            aria-pressed={response === 'resisted'}
            className={cn(
              'flex items-center justify-center gap-1.5 py-2 rounded-md border text-sm transition-colors',
              response === 'resisted'
                ? 'border-emerald-500 bg-emerald-500/10 text-emerald-600'
                : 'border-input text-muted-foreground hover:bg-accent',
            )}
          >
            <Shield className="w-4 h-4" />
            <span dir="auto">{t('journey.urge.resisted')}</span>
          </button>
          <button
            type="button"
            onClick={() => setResponse('lapsed')}
            aria-pressed={response === 'lapsed'}
            className={cn(
              'flex items-center justify-center gap-1.5 py-2 rounded-md border text-sm transition-colors',
              response === 'lapsed'
                ? 'border-amber-500 bg-amber-500/10 text-amber-600'
                : 'border-input text-muted-foreground hover:bg-accent',
            )}
          >
            <AlertTriangle className="w-4 h-4" />
            <span dir="auto">{t('journey.urge.lapsed')}</span>
          </button>
        </div>
      </div>

      <input
        type="text"
        value={triggerText}
        onChange={(e) => setTriggerText(e.target.value)}
        placeholder={t('journey.urge.triggerPlaceholder')}
        maxLength={80}
        className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm"
      />
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder={t('journey.urge.notesPlaceholder')}
        rows={2}
        className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm resize-none"
      />

      <div className="flex gap-2">
        <Button type="submit" disabled={submitting} className="flex-1">
          {t('common.save')}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}

// ----- Daily check-in card --------------------------------------------
function CheckinCard({ checkin, onSave, submitting }) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(!checkin)
  const [mood, setMood] = useState(checkin?.mood ?? 3)
  const [energy, setEnergy] = useState(checkin?.energy ?? 3)
  const [urgesToday, setUrgesToday] = useState(checkin?.urges_today ?? false)
  const [notes, setNotes] = useState(checkin?.notes ?? '')

  const startEdit = () => {
    setMood(checkin?.mood ?? 3)
    setEnergy(checkin?.energy ?? 3)
    setUrgesToday(checkin?.urges_today ?? false)
    setNotes(checkin?.notes ?? '')
    setEditing(true)
  }

  const submit = async (e) => {
    e.preventDefault()
    const ok = await onSave({ mood, energy, urges_today: urgesToday, notes })
    if (ok) setEditing(false)
  }

  return (
    <Card>
      <CardContent className="p-3 sm:p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold flex items-center gap-1.5" dir="auto">
            <BookOpen className="w-4 h-4 text-app-primary-500" />
            {t('journey.checkin.title')}
          </h2>
          {checkin && !editing && (
            <Button size="sm" variant="outline" onClick={startEdit}>
              {t('common.edit')}
            </Button>
          )}
        </div>

        {editing ? (
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <RatingPicker
                label={t('journey.checkin.mood')}
                value={mood}
                onChange={setMood}
              />
              <RatingPicker
                label={t('journey.checkin.energy')}
                value={energy}
                onChange={setEnergy}
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={urgesToday}
                onChange={(e) => setUrgesToday(e.target.checked)}
                className="w-4 h-4"
              />
              <span dir="auto">{t('journey.checkin.urgesToday')}</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('journey.checkin.notesPlaceholder')}
              rows={2}
              className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm resize-none"
            />
            <Button type="submit" disabled={submitting} className="w-full">
              {t('common.save')}
            </Button>
          </form>
        ) : (
          <div className="text-xs text-muted-foreground space-y-1" dir="auto">
            <p>{t('journey.checkin.mood')}: {checkin.mood ?? '—'}/5  ·  {t('journey.checkin.energy')}: {checkin.energy ?? '—'}/5</p>
            <p>{checkin.urges_today ? `⚠️ ${t('journey.checkin.urgesToday')}` : `✓ ${t('journey.checkin.noUrges')}`}</p>
            {checkin.notes && <p className="whitespace-pre-wrap pt-1 italic">{checkin.notes}</p>}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function RatingPicker({ label, value, onChange }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-1" dir="auto">{label}</p>
      <div className="grid grid-cols-5 gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            aria-pressed={value === n}
            className={cn(
              'py-1.5 rounded-md border text-xs transition-colors',
              value === n
                ? 'border-app-primary-500 bg-app-primary-500/10 text-app-primary-500'
                : 'border-input text-muted-foreground hover:bg-accent',
            )}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  )
}

// ----- Dashboard stats card -------------------------------------------
function StatsCard({ stats }) {
  const { t } = useTranslation()
  const rate7 = stats.urges7Rate
  const rate30 = stats.urges30Rate

  return (
    <Card>
      <CardContent className="p-3 sm:p-4 space-y-3">
        <h2 className="text-sm font-semibold flex items-center gap-1.5" dir="auto">
          <TrendingUp className="w-4 h-4 text-app-primary-500" />
          {t('journey.stats.title')}
        </h2>

        <div className="grid grid-cols-2 gap-2">
          <StatTile
            icon={Shield}
            label={t('journey.stats.resistanceRate7')}
            value={rate7 == null ? '—' : `${num(rate7)}%`}
            sub={rate7 == null ? null : `${stats.urges7Resisted} / ${stats.urges7Count}`}
            tone="positive"
          />
          <StatTile
            icon={Shield}
            label={t('journey.stats.resistanceRate30')}
            value={rate30 == null ? '—' : `${num(rate30)}%`}
            sub={rate30 == null ? null : `${stats.urges30Resisted} / ${stats.urges30Count}`}
            tone="positive"
          />
          <StatTile
            icon={Heart}
            label={t('journey.stats.daysSinceLapse')}
            value={stats.daysSinceLapse == null ? '—' : num(stats.daysSinceLapse)}
            sub={stats.daysSinceLapse == null ? t('journey.stats.noLapseYet') : null}
            tone="neutral"
          />
          <StatTile
            icon={BookOpen}
            label={t('journey.stats.checkins')}
            value={num(stats.checkinsCount)}
            tone="neutral"
          />
        </div>

        {stats.topTriggers.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground mb-1.5" dir="auto">
              {t('journey.stats.topTriggers')}
            </p>
            <ul className="text-xs space-y-0.5">
              {stats.topTriggers.map((tt) => (
                <li key={tt.trigger} className="flex justify-between gap-2">
                  <span className="truncate" dir="auto">{tt.trigger}</span>
                  <span className="text-muted-foreground font-mono" dir="ltr">{tt.count}×</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function StatTile({ icon: Icon, label, value, sub, tone }) {
  const toneClass = tone === 'positive'
    ? 'text-emerald-600'
    : tone === 'negative'
    ? 'text-red-600'
    : 'text-foreground'
  return (
    <div className="p-2 rounded-md border border-border/60 bg-muted/30">
      <div className="flex items-center gap-1 text-[10px] text-muted-foreground" dir="auto">
        <Icon className="w-3 h-3" />
        <span className="truncate">{label}</span>
      </div>
      <p className={cn('text-base font-semibold mt-0.5 font-mono', toneClass)} dir="ltr">{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground font-mono mt-0.5" dir="ltr">{sub}</p>}
    </div>
  )
}

// ----- Urge log list --------------------------------------------------
function UrgeRow({ urge, onDelete }) {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const isLapse = urge.response === 'lapsed'
  const Icon = isLapse ? AlertTriangle : Shield
  const tone = isLapse ? 'text-amber-600' : 'text-emerald-600'

  const handleDelete = async () => {
    const ok = await confirm({
      title: t('journey.urge.confirmDelete'),
      confirmLabel: t('common.delete'),
      variant: 'destructive',
      icon: Trash2,
    })
    if (ok) await onDelete(urge.id)
  }

  return (
    <Card>
      <CardContent className="p-3">
        <div className="flex items-start gap-2">
          <Icon className={cn('w-4 h-4 mt-0.5 flex-shrink-0', tone)} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold" dir="auto">
              {isLapse ? t('journey.urge.lapsed') : t('journey.urge.resisted')}
              {urge.intensity != null && (
                <span className="text-xs text-muted-foreground ms-1.5">· {urge.intensity}/5</span>
              )}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5 font-mono" dir="ltr">
              {new Date(urge.recorded_at).toLocaleString('en-CA', {
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit', hour12: false,
              })}
            </p>
            {urge.trigger_text && (
              <p className="text-xs text-foreground mt-1" dir="auto">{urge.trigger_text}</p>
            )}
            {urge.notes && (
              <p className="text-[11px] text-muted-foreground mt-1 whitespace-pre-wrap italic" dir="auto">
                {urge.notes}
              </p>
            )}
          </div>
          <RowActionsMenu
            ariaLabel={t('common.actions')}
            actions={[
              { icon: Trash2, label: t('common.delete'), onClick: handleDelete, variant: 'destructive' },
            ]}
          />
        </div>
      </CardContent>
    </Card>
  )
}

// ----- Page ------------------------------------------------------------
export function JourneyPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const goBack = () => navigate('/')

  // Hard redirect for non-owners. The route is also hidden from
  // ProfileDropdown for them; this is the second line of defence.
  if (user && !isOwner(user)) {
    return <Navigate to="/" replace />
  }

  const urgesHook = useUrges()
  const checkinsHook = useCheckins()
  const stats = useJourneyStats(urgesHook.urges, checkinsHook.checkins)

  const [logging, setLogging] = useState(false)
  const [savingUrge, setSavingUrge] = useState(false)
  const [savingCheckin, setSavingCheckin] = useState(false)

  const handleSaveUrge = async (values) => {
    setSavingUrge(true)
    const ok = await urgesHook.log(values)
    setSavingUrge(false)
    if (ok) setLogging(false)
  }

  const handleSaveCheckin = async (values) => {
    setSavingCheckin(true)
    const ok = await checkinsHook.upsert(values)
    setSavingCheckin(false)
    return ok
  }

  const handleRefresh = useCallback(
    () => performRefresh([urgesHook.refetch, checkinsHook.refetch]),
    [urgesHook.refetch, checkinsHook.refetch],
  )

  const tableMissing = urgesHook.tableMissing || checkinsHook.tableMissing

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-3 content-fade pb-8">
        <div className="flex items-center gap-2 mb-2">
          <button
            type="button"
            onClick={goBack}
            aria-label={t('settings.back')}
            className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
          >
            <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-foreground flex items-center gap-2" dir="auto">
              <Compass className="w-5 h-5 text-app-primary-500" />
              {t('journey.title')}
            </h1>
            <p className="text-[11px] text-muted-foreground" dir="auto">{t('journey.subtitle')}</p>
          </div>
        </div>

        {tableMissing && (
          <Card>
            <CardContent className="p-3 text-xs text-amber-700 dark:text-amber-400" dir="auto">
              {t('journey.tableMissing')}
            </CardContent>
          </Card>
        )}

        <StatsCard stats={stats} />

        <CheckinCard
          checkin={checkinsHook.todayCheckin}
          onSave={handleSaveCheckin}
          submitting={savingCheckin}
        />

        <VoiceJournalCard />

        <section className="space-y-2 pt-1">
          <div className="flex items-center justify-between px-1">
            <h2 className="text-sm font-semibold flex items-center gap-1.5" dir="auto">
              <Compass className="w-4 h-4 text-app-primary-500" />
              {t('journey.urge.title')}
            </h2>
            {!logging && (
              <Button size="sm" variant="outline" onClick={() => setLogging(true)}>
                <Plus className="w-4 h-4 me-1" />
                {t('journey.urge.add')}
              </Button>
            )}
          </div>

          {logging && (
            <UrgeForm
              onSave={handleSaveUrge}
              onCancel={() => setLogging(false)}
              submitting={savingUrge}
            />
          )}

          {urgesHook.loading ? (
            <Skeleton className="h-16 w-full" />
          ) : urgesHook.urges.length === 0 && !logging ? (
            <EmptyState icon={CheckCircle2} title={t('journey.urge.empty')} />
          ) : (
            urgesHook.urges.slice(0, 50).map((u) => (
              <UrgeRow key={u.id} urge={u} onDelete={urgesHook.remove} />
            ))
          )}
        </section>
      </div>
    </PullToRefresh>
  )
}
